import { motion } from "framer-motion";
import { Check, TrendingUp, ArrowRight } from "lucide-react";
import * as Icons from "lucide-react";
import { MOBILE_APPS } from "@/lib/data";
import { Reveal, Cascade } from "@/components/Reveal";
import { useDemoModal } from "@/components/DemoModalContext";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} strokeWidth={1.6} />;
};

/* ---------- Phone mockup screens ---------- */
function PatronScreen() {
  return (
    <div className="flex h-full flex-col bg-cloud">
      <div className="bg-gradient-to-br from-[#ee7f2e] to-[#d96f22] px-4 pb-5 pt-6 text-white">
        <p className="text-[10px] uppercase tracking-widest text-white/80">Patron Ekranı</p>
        <p className="mt-1 text-xs text-white/70">Bugünkü Ciro</p>
        <p className="text-2xl font-extrabold">₺184.250</p>
        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold">
          <TrendingUp className="h-3 w-3" /> %12 ▲
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 p-3">
        <div className="rounded-xl bg-white p-2.5 shadow-sm">
          <p className="text-[9px] text-ink/50">Net Kâr</p>
          <p className="text-sm font-bold text-[#7fa021]">₺62.400</p>
        </div>
        <div className="rounded-xl bg-white p-2.5 shadow-sm">
          <p className="text-[9px] text-ink/50">Çek / Senet</p>
          <p className="text-sm font-bold text-ink">₺28.900</p>
        </div>
      </div>
      <div className="mx-3 flex-1 rounded-xl bg-white p-3 shadow-sm">
        <p className="mb-2 text-[9px] text-ink/50">Haftalık Kâr/Zarar</p>
        <div className="flex h-16 items-end gap-1.5">
          {[40, 65, 50, 80, 60, 92, 74].map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm bg-gradient-to-t from-orange to-[#f2a15e]"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
      </div>
      <div className="p-3 text-center text-[9px] text-ink/40">AKINSOFT canlı veri • 12:41</div>
    </div>
  );
}

function StokScreen() {
  return (
    <div className="flex h-full flex-col bg-ink">
      <div className="px-4 pb-3 pt-6 text-white">
        <p className="text-[10px] uppercase tracking-widest text-[#a3c735]">Stok Sayım</p>
        <p className="mt-1 text-xs text-white/60">Depo A — Aktif Sayım</p>
      </div>
      <div className="mx-4 rounded-2xl border-2 border-dashed border-[#a3c735]/60 bg-white/5 p-4">
        <div className="relative h-20 overflow-hidden rounded-lg bg-white/10">
          <div className="absolute inset-x-3 top-1/2 flex -translate-y-1/2 items-end justify-center gap-[3px]">
            {[7, 12, 5, 14, 6, 11, 4, 13, 8, 12, 5, 10, 7, 14].map((w, i) => (
              <span key={i} className="w-[3px] bg-white" style={{ height: `${w * 4}px` }} />
            ))}
          </div>
          <motion.div
            animate={{ top: ["12%", "78%", "12%"] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-x-2 h-0.5 rounded bg-[#a3c735] shadow-[0_0_10px_#a3c735]"
          />
        </div>
        <p className="mt-2 text-center text-[10px] text-white/60">Barkod okutuluyor…</p>
      </div>
      <div className="mt-3 flex-1 space-y-1.5 px-4">
        {[
          ["Ürün #A1024", "48 adet"],
          ["Ürün #B2210", "12 adet"],
          ["Ürün #C0087", "126 adet"],
        ].map(([n, c]) => (
          <div key={n} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2">
            <span className="text-[11px] text-white/80">{n}</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#a3c735]">
              <Check className="h-3 w-3" /> {c}
            </span>
          </div>
        ))}
      </div>
      <div className="p-3">
        <div className="rounded-lg bg-[#a3c735] py-2 text-center text-[11px] font-bold text-ink">
          AKINSOFT'a Aktar
        </div>
      </div>
    </div>
  );
}

function Phone({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="relative mx-auto w-[260px]"
    >
      <motion.div
        animate={{ y: [0, -12, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="relative rounded-[2.6rem] border border-ink/10 bg-ink p-2.5 shadow-[0_30px_70px_rgba(32,32,32,0.22)]"
      >
        <div className="absolute left-1/2 top-2.5 z-10 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-ink" />
        <div className="h-[510px] overflow-hidden rounded-[2.1rem] bg-white">{children}</div>
      </motion.div>
    </motion.div>
  );
}

const SCREENS = { "patron-ekrani": <PatronScreen />, "stok-sayim": <StokScreen /> };

export default function MobileApps() {
  const { setOpen } = useDemoModal();
  return (
    <section
      id="mobil-uygulamalar"
      className="relative scroll-mt-24 overflow-hidden bg-white py-[100px] md:py-[140px]"
      data-testid="mobile-apps"
    >
      <div className="container-shell">
        {/* Header */}
        <Reveal className="mx-auto max-w-3xl text-center">
          <p className="overline">Bize Özel Mobil Uygulamalar</p>
          <h2 className="heading-lg mt-4">
            <Cascade text="AKINSOFT'a {%100 entegre} kendi mobil uygulamalarımız." />
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-ink/60">
            Sadece yazılım satmıyoruz; kendi bünyemizde geliştirdiğimiz mobil uygulamalarla
            AKINSOFT verilerinizi cebinize taşıyoruz.
          </p>
        </Reveal>

        {/* App rows */}
        <div className="mt-[80px] space-y-[80px] md:space-y-[120px]">
          {MOBILE_APPS.map((app, idx) => {
            const isGreen = app.accent === "green";
            const flip = idx % 2 === 1;
            return (
              <div
                key={app.key}
                className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20"
                data-testid={`mobile-app-${app.key}`}
              >
                {/* Phone */}
                <div className={`relative ${flip ? "lg:order-2" : ""}`}>
                  <div
                    className={`absolute left-1/2 top-1/2 -z-0 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl ${
                      isGreen ? "bg-lime/20" : "bg-orange/20"
                    }`}
                  />
                  <Phone>{SCREENS[app.key]}</Phone>
                </div>

                {/* Text */}
                <Reveal className={flip ? "lg:order-1" : ""}>
                  <span
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider ${
                      isGreen ? "bg-lime/15 text-[#6f8f1e]" : "bg-orange/10 text-orange"
                    }`}
                  >
                    <Icon name={app.icon} className="h-4 w-4" /> {app.badge}
                  </span>
                  <h3 className="mt-5 text-3xl font-extrabold tracking-tight text-ink md:text-4xl">
                    {app.title}
                  </h3>
                  <p className="mt-2 text-lg font-semibold text-ink/70">{app.tagline}</p>
                  <p className="mt-4 text-base leading-relaxed text-ink/60">{app.desc}</p>

                  <ul className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {app.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                            isGreen ? "bg-lime/20 text-[#6f8f1e]" : "bg-orange/15 text-orange"
                          }`}
                        >
                          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </span>
                        <span className="text-sm leading-snug text-ink/80">{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => setOpen(true)}
                    className="btn-primary mt-8"
                    data-testid={`mobile-app-demo-${app.key}`}
                  >
                    Demo Talebi Oluştur <ArrowRight className="h-4 w-4" />
                  </button>
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
